import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardVisual } from "@/components/CardVisual";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminListingDialog } from "@/components/AdminListingDialog";
import { AdminImportCsv } from "@/components/AdminImportCsv";
import { AdminAccountDialog } from "@/components/AdminAccountDialog";
import { ContactMessagesPanel } from "@/components/ContactMessagesPanel";
import { SeoIndexPanel } from "@/components/SeoIndexPanel";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const myt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("ms-MY", {
        timeZone: "Asia/Kuala_Lumpur",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const STATUS_TABS = [
  { value: "pending_payment", label: "Menunggu Bayaran" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "Semua" },
  { value: "payments", label: "Log Pembayaran" },
  { value: "claim_payments", label: "Tuntutan Berbayar" },
  { value: "coupons", label: "Kupon" },
  { value: "contact", label: "Pertanyaan" },
  { value: "claims", label: "Tuntutan" },
  { value: "seo", label: "Indeks SEO" },
];

const paymentTone = {
  paid: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-900",
  failed: "bg-red-100 text-red-800",
};

const badgeTone = {
  approved: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-900",
  pending_payment: "bg-sky-100 text-sky-900",
  rejected: "bg-red-100 text-red-800",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("pending_payment");
  const [listings, setListings] = useState([]);
  const [payments, setPayments] = useState({ items: [], totals: null });
  const [claims, setClaims] = useState([]);
  const [claimPayments, setClaimPayments] = useState({ items: [], totals: null });
  const [coupons, setCoupons] = useState([]);
  const [contact, setContact] = useState({ items: [], unread: 0 });
  const [locations, setLocations] = useState({ states: {}, categories: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("ALL");
  const [priceInput, setPriceInput] = useState("");
  const [galleryPriceInput, setGalleryPriceInput] = useState("");
  const [planPrices, setPlanPrices] = useState({});
  const [bundlePrices, setBundlePrices] = useState({});
  const [savingPrice, setSavingPrice] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data)).catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st, pub] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/stats"),
        api.get("/settings/public"),
      ]);
      setSettings({ ...pub.data, ...s.data, featured_plans: pub.data.featured_plans, max_photos: pub.data.max_photos });
      setStats(st.data);
      if (status === "payments") {
        const { data } = await api.get("/admin/payments");
        setPayments(data);
      } else if (status === "claims") {
        const { data } = await api.get("/admin/claims");
        setClaims(data.items);
      } else if (status === "claim_payments") {
        const { data } = await api.get("/admin/claim-payments");
        setClaimPayments(data);
      } else if (status === "coupons") {
        const { data } = await api.get("/admin/coupons");
        setCoupons(data.items);
      } else if (status === "contact") {
        const { data } = await api.get("/admin/contact-messages");
        setContact(data);
      } else {
        const { data } = await api.get("/admin/listings", {
          params: {
            status,
            q: search || undefined,
            state: stateFilter === "ALL" ? undefined : stateFilter,
          },
        });
        setListings(data.items);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [status, search, stateFilter]);

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
  }, [user, loadAll]);

  if (user === null) return <div className="grid h-72 place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const togglePaywall = async (checked) => {
    try {
      const { data } = await api.put("/admin/settings", { paywall_enabled: checked });
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success(checked ? `Paywall ${settings?.price_label || ""} DIHIDUPKAN` : "Mod Promosi Percuma AKTIF");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const savePrice = async () => {
    const ringgit = Number(priceInput);
    if (!ringgit || ringgit < 1 || ringgit > 1000) {
      toast.error("Harga mesti antara RM1 dan RM1000");
      return;
    }
    setSavingPrice(true);
    try {
      const { data } = await api.put("/admin/settings", {
        paywall_enabled: !!settings?.paywall_enabled,
        price_sen: Math.round(ringgit * 100),
      });
      setSettings((prev) => ({ ...prev, ...data }));
      setPriceInput("");
      toast.success(`Harga senaraian ditukar ke RM${(data.price_sen / 100).toFixed(0)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingPrice(false);
    }
  };

  const saveGalleryPrice = async () => {
    const ringgit = Number(galleryPriceInput);
    if (!ringgit || ringgit < 1 || ringgit > 1000) {
      toast.error("Harga galeri mesti antara RM1 dan RM1000");
      return;
    }
    setSavingPrice(true);
    try {
      const { data } = await api.put("/admin/settings", {
        paywall_enabled: !!settings?.paywall_enabled,
        gallery_price_sen: Math.round(ringgit * 100),
      });
      setSettings((prev) => ({ ...prev, ...data }));
      setGalleryPriceInput("");
      toast.success(`Harga galeri ditukar ke RM${(data.gallery_price_sen / 100).toFixed(0)}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingPrice(false);
    }
  };

  const savePlanPrices = async () => {
    const payload = { paywall_enabled: !!settings?.paywall_enabled };
    for (const key of ["1m", "2m", "6m"]) {
      for (const [state, field] of [[planPrices, "featured"], [bundlePrices, "bundle"]]) {
        const raw = state[key];
        if (!raw) continue;
        const ringgit = Number(raw);
        if (!ringgit || ringgit < 1 || ringgit > 1000) {
          toast.error("Setiap harga mesti antara RM1 dan RM1000");
          return;
        }
        payload[`${field}_${key}_sen`] = Math.round(ringgit * 100);
      }
    }
    if (Object.keys(payload).length === 1) {
      toast.error("Tiada harga baru dimasukkan");
      return;
    }
    setSavingPrice(true);
    try {
      await api.put("/admin/settings", payload);
      const { data } = await api.get("/settings/public");
      setSettings((prev) => ({ ...prev, ...data }));
      setPlanPrices({});
      setBundlePrices({});
      toast.success("Harga Featured & bundle dikemas kini");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingPrice(false);
    }
  };

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/listings/${id}/${action}`);
      toast.success(action === "approve" ? "Senarai diluluskan" : "Senarai ditolak");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeListing = async (listing) => {
    try {
      await api.delete(`/admin/listings/${listing.id}`);
      toast.success(`"${listing.business_name}" dibuang`);
      setDeleting(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const resolveClaim = async (listingId, action) => {
    try {
      await api.post(`/admin/claims/${listingId}/${action}`);
      toast.success(action === "approve" ? "Tuntutan diluluskan" : "Tuntutan ditolak");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Admin</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-600">{user.email}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            data-testid="admin-add-listing-open"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" /> Tambah senarai
          </Button>
          <AdminImportCsv onImported={loadAll} />
          <AdminAccountDialog currentEmail={user.email} />
          <Button variant="outline" size="sm" data-testid="admin-refresh-button" onClick={loadAll}>
            <RefreshCw className="mr-1.5 h-4 w-4" /> Muat semula
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="admin-logout-button"
            onClick={async () => {
              await logout();
              navigate("/admin/login");
            }}
          >
            <LogOut className="mr-1.5 h-4 w-4" /> Keluar
          </Button>
        </div>
      </div>

      <section className="mt-8 rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div>
            <Label htmlFor="paywall-toggle" className="font-display text-lg font-bold tracking-tight">
              Enable {settings?.price_label || "RM5"} Paywall
            </Label>
            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-slate-600">
              ON: pengguna perlu bayar {settings?.price_label || "RM5"} melalui toyyibPay (FPX) sebelum senarai LIVE. OFF (Free Promotion Mode):
              borang terus diluluskan dan LIVE secara percuma.
            </p>
            {settings && !settings.payment_gateway_ready && (
              <p data-testid="gateway-warning" className="mt-2 text-xs font-medium text-amber-700">
                Nota: kunci toyyibPay (userSecretKey / categoryCode) masih placeholder — paywall tidak akan dapat
                menjana bil.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-500">{settings?.paywall_enabled ? "ON" : "OFF"}</span>
            <Switch
              id="paywall-toggle"
              data-testid="paywall-toggle"
              checked={!!settings?.paywall_enabled}
              onCheckedChange={togglePaywall}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-end gap-4 border-t border-border pt-6">
          <div>
            <Label htmlFor="listing-price" className="text-sm font-semibold">
              Harga senaraian (RM)
            </Label>
            <p className="mt-1 text-xs text-slate-500">
              Harga semasa:{" "}
              <strong data-testid="current-price-label">
                RM{settings ? (settings.price_sen / 100).toFixed(0) : "—"}
              </strong>{" "}
              setiap senarai.
            </p>
            <Input
              id="listing-price"
              data-testid="listing-price-input"
              inputMode="decimal"
              placeholder={settings ? (settings.price_sen / 100).toFixed(0) : "5"}
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value.replace(/[^\d.]/g, ""))}
              className="mt-2 h-11 w-40"
            />
          </div>
          <Button
            data-testid="save-price-button"
            disabled={savingPrice || !priceInput}
            onClick={savePrice}
            className="h-11"
          >
            {savingPrice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Simpan harga
          </Button>

          <div>
            <Label htmlFor="gallery-price" className="text-sm font-semibold">
              Harga naik taraf galeri (RM)
            </Label>
            <p className="mt-1 text-xs text-slate-500">
              Semasa:{" "}
              <strong data-testid="current-gallery-price-label">
                {settings ? `RM${(settings.gallery_price_sen / 100).toFixed(0)}` : "—"}
              </strong>{" "}
              untuk buka galeri penuh.
            </p>
            <Input
              id="gallery-price"
              data-testid="gallery-price-input"
              inputMode="decimal"
              placeholder={settings ? (settings.gallery_price_sen / 100).toFixed(0) : "10"}
              value={galleryPriceInput}
              onChange={(e) => setGalleryPriceInput(e.target.value.replace(/[^\d.]/g, ""))}
              className="mt-2 h-11 w-40"
            />
          </div>
          <Button
            data-testid="save-gallery-price-button"
            disabled={savingPrice || !galleryPriceInput}
            onClick={saveGalleryPrice}
            className="h-11"
          >
            Simpan harga galeri
          </Button>
        </div>

        <div className="mt-6 border-t border-border pt-6">
          <h3 className="font-display text-base font-bold tracking-tight">
            Harga Featured &amp; pakej bundle
          </h3>
          <p className="mt-1.5 text-sm text-slate-600">
            Bundle = Featured + galeri {settings?.max_photos || 7} gambar. Biarkan kosong untuk kekalkan
            harga semasa.
          </p>
          <div className="mt-5 grid gap-5 sm:grid-cols-3">
            {(settings?.featured_plans || []).map((p) => (
              <div key={p.key} className="rounded-lg border border-border p-4">
                <p className="font-display text-sm font-bold tracking-tight">{p.label}</p>
                <p className="mt-1 text-xs text-slate-500" data-testid={`plan-price-label-${p.key}`}>
                  Featured {p.price_label} · Bundle {p.bundle_price_label} (jimat {p.bundle_saving_label})
                </p>
                <Label htmlFor={`featured-${p.key}`} className="mt-3 block text-xs font-semibold">
                  Featured (RM)
                </Label>
                <Input
                  id={`featured-${p.key}`}
                  data-testid={`featured-price-input-${p.key}`}
                  inputMode="decimal"
                  placeholder={(p.price_sen / 100).toFixed(0)}
                  value={planPrices[p.key] ?? (p.price_sen / 100).toFixed(0)}
                  onChange={(e) =>
                    setPlanPrices((prev) => ({ ...prev, [p.key]: e.target.value.replace(/[^\d.]/g, "") }))
                  }
                  className="mt-1.5 h-10"
                />
                <Label htmlFor={`bundle-${p.key}`} className="mt-3 block text-xs font-semibold">
                  Bundle (RM)
                </Label>
                <Input
                  id={`bundle-${p.key}`}
                  data-testid={`bundle-price-input-${p.key}`}
                  inputMode="decimal"
                  placeholder={(p.bundle_price_sen / 100).toFixed(0)}
                  value={bundlePrices[p.key] ?? (p.bundle_price_sen / 100).toFixed(0)}
                  onChange={(e) =>
                    setBundlePrices((prev) => ({ ...prev, [p.key]: e.target.value.replace(/[^\d.]/g, "") }))
                  }
                  className="mt-1.5 h-10"
                />
              </div>
            ))}
          </div>
          <Button
            data-testid="save-plan-prices-button"
            disabled={savingPrice}
            onClick={savePlanPrices}
            className="mt-5 h-11"
          >
            {savingPrice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Simpan harga Featured &amp; bundle
          </Button>
        </div>
      </section>

      {stats && (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5">
          {[
            ["approved", "Approved"],
            ["pending", "Pending"],
            ["pending_payment", "Tunggu Bayaran"],
            ["rejected", "Rejected"],
            ["paid_payments", "Bayaran Diterima"],
          ].map(([key, label]) => (
            <div key={key} data-testid={`admin-stat-${key}`} className="rounded-lg border border-border bg-card p-5">
              <p className="font-display text-2xl font-bold">{stats[key]}</p>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList data-testid="admin-status-tabs" className="flex-wrap">
            {STATUS_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`admin-tab-${t.value}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {!["payments", "claims", "claim_payments", "coupons", "contact", "seo"].includes(status) && (
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              data-testid="admin-search-input"
              placeholder="Cari nama, telefon, daerah atau emel…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 sm:max-w-sm"
            />
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger data-testid="admin-state-filter" className="h-11 sm:w-56">
                <SelectValue placeholder="Semua negeri" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="ALL">Semua negeri</SelectItem>
                {Object.keys(locations.states || {}).map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(search || stateFilter !== "ALL") && (
              <Button
                variant="outline"
                data-testid="admin-clear-filters"
                onClick={() => {
                  setSearch("");
                  setStateFilter("ALL");
                }}
                className="h-11"
              >
                Kosongkan
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : status === "payments" ? (
          <div className="mt-6">
            {payments.totals && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  ["paid", "Dibayar"],
                  ["pending", "Tersekat di checkout"],
                  ["failed", "Gagal"],
                ].map(([key, label]) => (
                  <div key={key} data-testid={`payments-total-${key}`} className="rounded-lg border border-border bg-card p-5">
                    <p className="font-display text-2xl font-bold">{payments.totals[key]}</p>
                    <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{label}</p>
                  </div>
                ))}
                <div data-testid="payments-total-revenue" className="rounded-lg border border-border bg-card p-5">
                  <p className="font-display text-2xl font-bold">
                    RM{(payments.totals.revenue_sen / 100).toFixed(2)}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Jumlah kutipan</p>
                </div>
              </div>
            )}

            {payments.items.length === 0 ? (
              <p data-testid="payments-empty" className="mt-8 rounded-lg border border-dashed border-border p-10 text-center text-sm text-slate-500">
                Belum ada rekod pembayaran.
              </p>
            ) : (
              <div data-testid="payments-table" className="mt-6 overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="border-b border-border bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      {["Bil", "Perniagaan", "Emel", "Tujuan", "Jumlah", "Status", "Tarikh"].map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.items.map((p) => (
                      <tr key={p.bill_code} data-testid={`payment-row-${p.bill_code}`} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 font-mono text-xs">{p.bill_code}</td>
                        <td className="px-4 py-3">{p.business_name || "—"}</td>
                        <td className="px-4 py-3 text-slate-500">{p.owner_email || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-[11px]">{p.purpose || "listing"}</Badge>
                        </td>
                        <td className="px-4 py-3 font-semibold">RM{((p.amount_sen || 0) / 100).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-[11px] ${paymentTone[p.status] || "bg-slate-100 text-slate-700"}`}>
                            {p.status === "pending" ? "tersekat" : p.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{String(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : status === "contact" ? (
          <ContactMessagesPanel data={contact} reload={loadAll} formatDate={myt} />
        ) : status === "seo" ? (
          <SeoIndexPanel formatDate={myt} />
        ) : status === "coupons" ? (
          <div className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-5">
              <div>
                <p className="text-sm font-semibold text-slate-900">Kupon senaraian percuma</p>
                <p className="mt-1 text-xs text-slate-500">
                  Sah 24 jam, satu kali guna, untuk senaraian Asas sahaja (tanpa pakej promosi).
                </p>
              </div>
              <Button
                data-testid="create-coupon-button"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/coupons", {});
                    toast.success(`Kupon ${data.code} dicipta — sah 24 jam`);
                    loadAll();
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                  }
                }}
                className="bg-primary hover:bg-[#0052CC]"
              >
                Cipta kupon baharu
              </Button>
            </div>
            {coupons.length === 0 ? (
              <p data-testid="coupons-empty" className="mt-6 rounded-lg border border-dashed border-border p-10 text-center text-sm text-slate-500">
                Belum ada kupon.
              </p>
            ) : (
              <div data-testid="coupons-table" className="mt-6 overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full min-w-[620px] text-left text-sm">
                  <thead className="border-b border-border bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      {["Kod", "Status", "Tamat tempoh", "Digunakan"].map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((c) => (
                      <tr key={c.code} data-testid={`coupon-row-${c.code}`} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 font-mono font-semibold">{c.code}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-[11px] ${
                            c.status === "aktif" ? "bg-emerald-100 text-emerald-800"
                              : c.status === "digunakan" ? "bg-slate-200 text-slate-700"
                                : "bg-amber-100 text-amber-900"}`}>
                            {c.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{myt(c.expires_at)}</td>
                        <td className="px-4 py-3 text-slate-500">{c.used_at ? myt(c.used_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : status === "claim_payments" ? (
          <div className="mt-6">
            {claimPayments.totals && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  ["paid", "Bayaran diterima"],
                  ["pending", "Tersekat di checkout"],
                  ["completed", "Tuntutan selesai"],
                ].map(([key, label]) => (
                  <div key={key} data-testid={`claim-payments-total-${key}`} className="rounded-lg border border-border bg-card p-5">
                    <p className="font-display text-2xl font-bold">{claimPayments.totals[key]}</p>
                    <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{label}</p>
                  </div>
                ))}
                <div data-testid="claim-payments-total-revenue" className="rounded-lg border border-border bg-card p-5">
                  <p className="font-display text-2xl font-bold">
                    RM{(claimPayments.totals.revenue_sen / 100).toFixed(2)}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Kutipan tuntutan</p>
                </div>
              </div>
            )}
            {claimPayments.items.length === 0 ? (
              <p data-testid="claim-payments-empty" className="mt-8 rounded-lg border border-dashed border-border p-10 text-center text-sm text-slate-500">
                Belum ada tuntutan berbayar.
              </p>
            ) : (
              <div data-testid="claim-payments-table" className="mt-6 overflow-x-auto rounded-lg border border-border bg-card">
                <table className="w-full min-w-[820px] text-left text-sm">
                  <thead className="border-b border-border bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      {["Bil", "Perniagaan", "Emel penuntut", "Jumlah", "Bayaran", "Tuntutan", "Tarikh"].map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {claimPayments.items.map((c) => (
                      <tr key={c.bill_code} data-testid={`claim-payment-row-${c.bill_code}`} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 font-mono text-xs">{c.bill_code}</td>
                        <td className="px-4 py-3">
                          {c.slug ? (
                            <a href={`/business/${c.slug}`} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                              {c.business_name || "—"}
                            </a>
                          ) : (
                            c.business_name || "—"
                          )}
                          <span className="block text-xs text-slate-500">{c.location}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{c.claim_email || c.owner_email || "—"}</td>
                        <td className="px-4 py-3 font-semibold">RM{((c.amount_sen || 0) / 100).toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <Badge className={`text-[11px] ${paymentTone[c.status] || "bg-slate-100 text-slate-700"}`}>
                            {c.status === "pending" ? "tersekat" : c.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-[11px] ${c.claim_applied ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                            {c.claim_applied ? "selesai" : "belum"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{String(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : status === "claims" ? (
          claims.length === 0 ? (
            <p data-testid="claims-empty" className="mt-8 rounded-lg border border-dashed border-border p-10 text-center text-sm text-slate-500">
              Tiada permintaan tuntutan.
            </p>
          ) : (
            <div data-testid="claims-table" className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
              {claims.map((c) => (
                <div key={`${c.listing_id}-${c.created_at}`} data-testid={`claim-row-${c.listing_id}`} className="flex flex-col gap-4 border-b border-border p-5 last:border-0 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold tracking-tight">{c.business_name}</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {c.phone_number} · minta oleh <strong>{c.requested_email}</strong>
                    </p>
                    <Badge className={`mt-2 text-[11px] ${c.status === "pending" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"}`}>
                      {c.status}
                    </Badge>
                  </div>
                  {c.status === "pending" && (
                    <div className="flex gap-2">
                      <Button size="sm" data-testid={`claim-approve-${c.listing_id}`} onClick={() => resolveClaim(c.listing_id, "approve")} className="bg-emerald-600 text-white hover:bg-emerald-700">
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`claim-reject-${c.listing_id}`} onClick={() => resolveClaim(c.listing_id, "reject")}>
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : listings.length === 0 ? (
          <p data-testid="admin-empty" className="mt-8 rounded-lg border border-dashed border-border p-10 text-center text-sm text-slate-500">
            Tiada senarai untuk status ini.
          </p>
        ) : (
          <div data-testid="admin-listings-table" className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
            {listings.map((l) => (
              <div key={l.id} data-testid={`admin-row-${l.id}`} className="flex flex-col gap-4 border-b border-border p-5 last:border-0 sm:flex-row sm:items-center">
                <div className="h-16 w-28 shrink-0 overflow-hidden rounded-md border border-border">
                  {l.business_card_image_url ? (
                    <img src={mediaUrl(l.business_card_image_url)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <CardVisual listing={l} compact />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold tracking-tight">{l.business_name}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {l.category} · {l.district}, {l.state} · {l.phone_number}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge className={`text-[11px] ${badgeTone[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status}</Badge>
                    {l.ocr_status && <Badge variant="outline" className="text-[11px]">OCR: {l.ocr_status}</Badge>}
                    <span className="text-xs text-slate-400">{(l.search_tags || []).length} tags</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    data-testid={`approve-button-${l.id}`}
                    onClick={() => moderate(l.id, "approve")}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`reject-button-${l.id}`} onClick={() => moderate(l.id, "reject")}>
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`edit-button-${l.id}`}
                    onClick={() => {
                      setEditing(l);
                      setDialogOpen(true);
                    }}
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Sunting
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`delete-button-${l.id}`}
                    onClick={() => setDeleting(l)}
                    className="border-red-200 text-red-700 hover:bg-red-600 hover:text-white"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AdminListingDialog
        locations={locations}
        listing={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={loadAll}
      />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Buang senarai ini?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.business_name}" akan dibuang terus dari direktori. Tindakan ini tidak boleh
              dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              onClick={() => removeListing(deleting)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Ya, buang
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
